import { components } from "react-select";
import {
  ClientAdvanceSourceType,
  ClientAdvanceSourceTypeLabel,
  ClientAdvanceStatus,
  clientAdvanceStatusMap,
} from "features/ClientAdvances/constants/client-advance.constants";
import type { ClientAdvanceSelectOption } from "features/ClientAdvances/types/client-advance.types";
import { formatCurrency } from "utils/currency";

function StatusBadge({ status }: { status: string | undefined }) {
  if (!status) return null;
  const entry = clientAdvanceStatusMap[status as ClientAdvanceStatus];
  if (!entry) return null;
  return (
    <span className={`badge bg-${entry.color}-subtle text-${entry.color} ms-1`}>
      {entry.label}
    </span>
  );
}

function sourceSummary(data: ClientAdvanceSelectOption): string | undefined {
  const typeLbl = data.sourceType
    ? (ClientAdvanceSourceTypeLabel[data.sourceType as ClientAdvanceSourceType] ?? data.sourceType)
    : undefined;
  const code = data.sourceCode?.trim();
  if (typeLbl && code) return `${typeLbl} · ${code}`;
  if (typeLbl) return typeLbl;
  if (code) return code;
  return undefined;
}

export function ClientAdvanceOption(props: any) {
  const data = props.data as ClientAdvanceSelectOption;
  const source = sourceSummary(data);
  const remaining =
    data.remainingAmount != null && Number.isFinite(data.remainingAmount)
      ? data.remainingAmount
      : null;
  const total =
    data.amount != null && Number.isFinite(data.amount) ? data.amount : null;

  return (
    <components.Option {...props}>
      <div className="d-flex flex-column py-1">
        <div className="d-flex align-items-center justify-content-between gap-2">
          <span className="fw-medium text-body">{data.label}</span>
          <StatusBadge status={data.status} />
        </div>
        {source ? (
          <small className="text-muted text-truncate">{source}</small>
        ) : null}
        {remaining !== null || total !== null ? (
          <div className="d-flex justify-content-between mt-1">
            {remaining !== null ? (
              <small className="text-muted">
                Remaining:{" "}
                <span className="text-body">{formatCurrency(remaining)}</span>
              </small>
            ) : (
              <span />
            )}
            {total !== null ? (
              <small className="text-muted">
                Total:{" "}
                <span className="text-body">{formatCurrency(total)}</span>
              </small>
            ) : null}
          </div>
        ) : null}
      </div>
    </components.Option>
  );
}

export function ClientAdvanceSingleValue(props: any) {
  const data = props.data as ClientAdvanceSelectOption;
  const remaining =
    data.remainingAmount != null && Number.isFinite(data.remainingAmount)
      ? data.remainingAmount
      : null;

  return (
    <components.SingleValue {...props}>
      <span className="d-inline-flex align-items-center gap-1 min-w-0 text-truncate">
        <span className="text-truncate">{data.label}</span>
        {remaining !== null ? (
          <span className="text-muted text-truncate small">
            · Remaining {formatCurrency(remaining)}
          </span>
        ) : null}
      </span>
    </components.SingleValue>
  );
}
